import React, { useRef, FC, FormEvent } from 'react';
import { Canvas } from '@react-three/fiber';
import { Suspense } from 'react';
import SocialLinks from '../SocialLinks';
import { personalInfo } from '../../utils/data';
import useSound from '../../hooks/useSound';

const Contact: FC = () => {
  const contactRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { playClick } = useSound();
  
  // Create element variables for Three.js components
  const AmbientLightElement = 'ambientLight' as any;
  const PointLightElement = 'pointLight' as any;
  
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    playClick();
    
    if (!formRef.current) return;
    
    // Get form data
    const formData = new FormData(formRef.current);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;
    
    // Email logic would go here
    // For now, just log it
    console.log({ name, email, message });
    
    // Show success message
    alert('Message sent successfully!');
    
    // Reset form
    formRef.current.reset();
  };
  
  return (
    <div className="contact-section" ref={contactRef}>
      <div className="contact-content">
        <h2 className="section-title">Contact Me</h2>
        
        <div className="contact-container">
          <div className="contact-form-container">
            <div className="contact-panel">
              <div className="panel-header">
                <span className="panel-icon">✉</span>
                <span className="panel-title">SEND MESSAGE</span>
              </div>
              
              <form className="contact-form" ref={formRef} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Enter your name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="Enter your email"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    placeholder="Type your message here..."
                    rows={5}
                  ></textarea>
                </div>
                
                <button type="submit" className="submit-btn" onClick={playClick}>
                  TRANSMIT
                </button>
              </form>
            </div>
          </div>
          
          <div className="contact-links-container">
            <Canvas
              shadows
              camera={{ position: [0, 0, 5], fov: 50 }}
            >
              <AmbientLightElement intensity={0.5} />
              <PointLightElement position={[10, 10, 10]} intensity={0.8} />
              
              <Suspense fallback={null}>
                <SocialLinks position={[0, 0, 0]} size={1.5} />
              </Suspense>
            </Canvas>
            
            <div className="contact-info">
              <div className="contact-info-item">
                <i className="fas fa-envelope"></i>
                <a href={`mailto:${personalInfo.email}`}>{personalInfo.email}</a>
              </div>
              
              <div className="contact-info-item">
                <i className="fas fa-globe"></i>
                <a href={personalInfo.social.website.url} target="_blank" rel="noopener noreferrer">
                  {personalInfo.social.website.name}
                </a>
              </div>
              
              <div className="contact-info-item">
                <i className="fas fa-map-marker-alt"></i>
                <span>{personalInfo.location}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>
        {`
        .contact-section {
          min-height: 100vh;
          width: 100%;
          display: flex;
          flex-direction: column;
          padding: 80px 0 0;
        }
        
        .contact-content {
          flex: 1;
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .section-title {
          font-size: 42px;
          color: #00ff00;
          margin-bottom: 40px;
          text-align: center;
          text-shadow: 0 0 10px rgba(0, 255, 0, 0.7);
          font-family: 'VT323', monospace;
          letter-spacing: 3px;
        }
        
        .contact-container {
          display: flex;
          justify-content: space-between;
          gap: 30px;
          flex-wrap: wrap;
        }
        
        .contact-form-container {
          flex: 1;
          min-width: 300px;
        }
        
        .contact-panel {
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          border-radius: 5px;
          overflow: hidden;
          box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
        }
        
        .panel-header {
          background-color: rgba(0, 50, 0, 0.5);
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
          border-bottom: 1px solid #00ff00;
        }
        
        .panel-icon {
          font-size: 20px;
          color: #00ff00;
        }
        
        .panel-title {
          font-family: 'VT323', monospace;
          font-size: 22px;
          letter-spacing: 2px;
          color: #00ff00;
        }
        
        .contact-form {
          padding: 20px;
        }
        
        .form-group {
          margin-bottom: 20px;
        }
        
        .form-group label {
          display: block;
          color: #00ff00;
          margin-bottom: 5px;
          font-size: 16px;
        }
        
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          color: #00ff00;
          font-family: 'Courier Prime', monospace;
          resize: none;
          border-radius: 3px;
          transition: all 0.3s;
        }
        
        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
        }
        
        .submit-btn {
          background-color: transparent;
          border: 2px solid #00ff00;
          color: #00ff00;
          padding: 12px 24px;
          font-family: 'VT323', monospace;
          font-size: 18px;
          letter-spacing: 2px;
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }
        
        .submit-btn:hover {
          background-color: rgba(0, 255, 0, 0.1);
          box-shadow: 0 0 20px rgba(0, 255, 0, 0.7);
        }
        
        .contact-links-container {
          flex: 1;
          min-width: 300px;
          height: 400px;
          display: flex;
          flex-direction: column;
        }
        
        .contact-info {
          background-color: rgba(0, 0, 0, 0.7);
          border: 1px solid #00ff00;
          border-radius: 5px;
          padding: 20px;
          margin-top: 30px;
          box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
        }
        
        .contact-info-item {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
          color: #00ff00;
        }
        
        .contact-info-item i {
          margin-right: 10px;
          font-size: 18px;
        }
        
        .contact-info-item a {
          color: #00ff00;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .contact-info-item a:hover {
          text-shadow: 0 0 5px #00ff00;
        }
        
      
        @media (max-width: 768px) {
          .contact-container {
            flex-direction: column;
          }
        }
        `}
      </style>
    </div>
  );
};

export default Contact;